// Game Details and Results Entry screen
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button, Input, Card, Avatar } from '../components';
import HamburgerMenu from '../components/HamburgerMenu';
import { getGame, updateGameStatus, drawPlayingOrder, resetGame, updateGame, deletePendingGame } from '../services/gameService';
import { deleteAllResultsForGame } from '../services/resultService';
import { getResultsForGame, saveGameResults, validateGameResults, updateEliminationOrder } from '../services/resultService';
import { rateGame, getPlayerRatingForGame, getAverageRatingForGame } from '../services/gameRatingService';
import { formatDateTime } from '../utils/dateHelpers';
import { generateTimeOptions } from '../utils/timeHelpers';
import { getCategoryLeaderboard } from '../utils/leaderboard';

const GameDetails = () => {
  const { competitionId, gameId } = useParams();
  const navigate = useNavigate();
  const { players, categories, games, results, isOrganizer, refreshCompetition, currentPlayer } = useCompetition();

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit mode state
  const [isEditingGame, setIsEditingGame] = useState(false);
  const [editedGameName, setEditedGameName] = useState('');
  const [editedCategoryId, setEditedCategoryId] = useState('');
  const [editedGameDate, setEditedGameDate] = useState('');
  const [editedGameTime, setEditedGameTime] = useState('');
  const [editedKnockoutMode, setEditedKnockoutMode] = useState(false);
  const [editedDurationMinutes, setEditedDurationMinutes] = useState('');
  // Separate date and time components for actual start/end times
  const [editedStartDate, setEditedStartDate] = useState('');
  const [editedStartHour, setEditedStartHour] = useState('');
  const [editedStartMinute, setEditedStartMinute] = useState('');
  const [editedEndDate, setEditedEndDate] = useState('');
  const [editedEndHour, setEditedEndHour] = useState('');
  const [editedEndMinute, setEditedEndMinute] = useState('');
  const [savingGameEdit, setSavingGameEdit] = useState(false);

  // Timer state
  const [startingTimer, setStartingTimer] = useState(false);
  const [endingTimer, setEndingTimer] = useState(false);
  const [clearingTimer, setClearingTimer] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Delete game state
  const [deletingGame, setDeletingGame] = useState(false);

  // Initialize results state (Option B: all default to participated)
  const [resultsData, setResultsData] = useState([]);

  // Knockout mode state
  const [eliminationOrder, setEliminationOrder] = useState([]);

  // Rating state
  const [playerRating, setPlayerRating] = useState(null);
  const [averageRating, setAverageRating] = useState({ average: 0, count: 0 });
  const [hoverRating, setHoverRating] = useState(0);
  const [savingRating, setSavingRating] = useState(false);

  useEffect(() => {
    loadGameData();
  }, [gameId]);

  // Update current time every second when timer is running
  useEffect(() => {
    if (game?.actualStartTime && !game?.actualEndTime) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [game?.actualStartTime, game?.actualEndTime]);

  // Load player's rating when currentPlayer is available
  useEffect(() => {
    const loadPlayerRating = async () => {
      if (currentPlayer && gameId) {
        const rating = await getPlayerRatingForGame(gameId, currentPlayer.id);
        setPlayerRating(rating?.rating || null);
      }
    };
    loadPlayerRating();
  }, [currentPlayer, gameId]);

  const loadGameData = async () => {
    try {
      setLoading(true);
      const gameData = await getGame(gameId);
      setGame(gameData);

      const results = await getResultsForGame(gameId);

      // Initialize results data for all players
      const initialData = players.map(player => {
        const existingResult = results.find(r => r.playerId === player.id);
        return {
          playerId: player.id,
          playerName: player.displayName,
          pointsEntered: existingResult?.pointsEntered ?? '',
          participated: existingResult?.participated ?? true,
          eliminationPosition: existingResult?.eliminationPosition ?? null
        };
      });

      setResultsData(initialData);

      // For knockout mode, initialize elimination order from results
      if (gameData.knockoutMode) {
        const participantResults = initialData.filter(r => r.participated && r.eliminationPosition);
        const sortedByElimination = participantResults.sort((a, b) => a.eliminationPosition - b.eliminationPosition);
        setEliminationOrder(sortedByElimination.map(r => r.playerId));
      }

      // Load rating data
      const avgRating = await getAverageRatingForGame(gameId);
      setAverageRating(avgRating);

      setLoading(false);
    } catch (err) {
      console.error('Error loading game:', err);
      setError('Failed to load game');
      setLoading(false);
    }
  };

  const category = useMemo(() => {
    return categories.find(c => c.id === game?.categoryId);
  }, [categories, game]);

  // Calculate preview average
  const averagePreview = useMemo(() => {
    const participantResults = resultsData.filter(
      r => r.participated && r.pointsEntered !== '' && !isNaN(r.pointsEntered)
    );

    if (participantResults.length === 0) return null;

    const sum = participantResults.reduce((acc, r) => acc + parseFloat(r.pointsEntered), 0);
    return sum / participantResults.length;
  }, [resultsData]);

  // Calculate category leaderboard and player insights
  const categoryStats = useMemo(() => {
    if (!game || !currentPlayer || !games || !results) return null;

    const categoryLeaderboard = getCategoryLeaderboard(players, games, results, game.categoryId);
    const playerStats = categoryLeaderboard.find(p => p.playerId === currentPlayer.id);

    if (!playerStats) return null;

    // Calculate what player needs to improve ranking
    const insights = [];

    // Find players ahead of current player
    const playersAhead = categoryLeaderboard.filter(p => p.rank < playerStats.rank);

    if (playersAhead.length > 0) {
      // Find the player just ahead
      const nextPlayerAhead = playersAhead[playersAhead.length - 1];
      const pointsNeeded = Math.ceil(nextPlayerAhead.score - playerStats.score + 1);

      if (pointsNeeded > 0) {
        insights.push({
          type: 'improve',
          message: `Score ${pointsNeeded} more placement point${pointsNeeded > 1 ? 's' : ''} to move up to rank ${nextPlayerAhead.rank}`,
          target: nextPlayerAhead.playerName,
          pointsNeeded
        });
      }
    }

    // Calculate best possible outcome for this game
    // Get all players participating in this game (based on resultsData)
    const participatingPlayerIds = resultsData
      .filter(r => r.participated)
      .map(r => r.playerId);

    let bestCaseBreakdown = null;

    if (participatingPlayerIds.length > 0 && participatingPlayerIds.includes(currentPlayer.id)) {
      // Create a scenario where:
      // - Current player finishes 1st (gets max points = number of participants)
      // - Players currently ranked higher finish in worst positions (ranked #1 gets last place, etc.)

      const numParticipants = participatingPlayerIds.length;

      // Get participating players sorted by current rank
      const participatingPlayers = categoryLeaderboard
        .filter(p => participatingPlayerIds.includes(p.playerId))
        .sort((a, b) => a.rank - b.rank);

      // Assign game positions: best ranked player gets worst position (1 point)
      const gameResults = [];

      // Get players ranked higher than current player (they will get worst positions)
      const higherRankedPlayers = participatingPlayers.filter(p => p.rank < playerStats.rank && p.playerId !== currentPlayer.id);
      // Get players ranked lower than current player (they will get middle positions)
      const lowerRankedPlayers = participatingPlayers.filter(p => p.rank > playerStats.rank && p.playerId !== currentPlayer.id);

      // Current player gets 1st place
      gameResults.push({
        playerId: playerStats.playerId,
        playerName: playerStats.playerName,
        currentRank: playerStats.rank,
        currentScore: playerStats.score,
        gamePosition: 1,
        gamePoints: numParticipants,
        newScore: playerStats.score + numParticipants
      });

      // Higher ranked players get worst positions (reverse order)
      // Player ranked #1 gets LAST place (1 point), #2 gets 2nd to last (2 points), etc.
      higherRankedPlayers.forEach((player, index) => {
        const gamePosition = numParticipants - index; // Last, 2nd to last, etc.
        const points = 1 + index; // 1 point for last, 2 for 2nd to last, etc.
        gameResults.push({
          playerId: player.playerId,
          playerName: player.playerName,
          currentRank: player.rank,
          currentScore: player.score,
          gamePosition: gamePosition,
          gamePoints: points,
          newScore: player.score + points
        });
      });

      // Lower ranked players get middle positions
      const middlePositionStart = 2; // Start after current player (who is 1st)
      lowerRankedPlayers.forEach((player, index) => {
        const gamePosition = middlePositionStart + index;
        const points = numParticipants - gamePosition + 1;
        gameResults.push({
          playerId: player.playerId,
          playerName: player.playerName,
          currentRank: player.rank,
          currentScore: player.score,
          gamePosition: gamePosition,
          gamePoints: points,
          newScore: player.score + points
        });
      });

      // Calculate new overall rankings after this game
      const bestCaseScenario = categoryLeaderboard.map(p => {
        const gameResult = gameResults.find(gr => gr.playerId === p.playerId);
        if (gameResult) {
          return { ...gameResult, actualScore: p.actualScore };
        }
        // Not playing this game
        return { ...p, newScore: p.score, gamePoints: 0, gamePosition: null };
      });

      // Re-sort by new placement point scores, with actualScore as tie-breaker
      bestCaseScenario.sort((a, b) => {
        if (b.newScore !== a.newScore) return b.newScore - a.newScore;
        return b.actualScore - a.actualScore;
      });

      // Assign new ranks
      let currentRank = 1;
      bestCaseScenario.forEach((player, index) => {
        if (index > 0) {
          const prev = bestCaseScenario[index - 1];
          // Rank changes if newScore is different OR if newScore is same but actualScore is different
          if (player.newScore < prev.newScore || (player.newScore === prev.newScore && player.actualScore < prev.actualScore)) {
            currentRank = index + 1;
          }
        }
        player.bestCaseRank = currentRank;
      });

      const bestCasePlayerStats = bestCaseScenario.find(p => p.playerId === currentPlayer.id);

      if (bestCasePlayerStats) {
        // Always show the breakdown to demonstrate what would happen with "perfect evening" results
        bestCaseBreakdown = {
          participatingPlayers: gameResults.sort((a, b) => a.gamePosition - b.gamePosition),
          newRankings: bestCaseScenario,
          newRank: bestCasePlayerStats.bestCaseRank,
          newScore: bestCasePlayerStats.newScore
        };

        // Only show the insight if it would improve the player's rank
        if (bestCasePlayerStats.bestCaseRank < playerStats.rank) {
          insights.push({
            type: 'bestcase',
            message: `Best outcome: Win this game to move up to rank #${bestCasePlayerStats.bestCaseRank} with ${bestCasePlayerStats.newScore} points`,
            newRank: bestCasePlayerStats.bestCaseRank,
            newScore: bestCasePlayerStats.newScore
          });
        }
      }
    }

    // If player is near the top, show encouragement
    if (playerStats.rank === 1) {
      insights.push({
        type: 'leading',
        message: `You're leading in ${category?.name}! Keep it up!`
      });
    } else if (playerStats.rank <= 3) {
      insights.push({
        type: 'podium',
        message: `You're on the podium! Maintain your position or aim for #1.`
      });
    }

    return {
      leaderboard: categoryLeaderboard,
      playerStats,
      insights,
      bestCaseBreakdown
    };
  }, [game, currentPlayer, games, results, players, category, resultsData]);

  const handlePointsChange = (playerId, value) => {
    setResultsData(prev =>
      prev.map(r =>
        r.playerId === playerId
          ? { ...r, pointsEntered: value }
          : r
      )
    );
  };

  const handleParticipationToggle = (playerId) => {
    setResultsData(prev =>
      prev.map(r =>
        r.playerId === playerId
          ? { ...r, participated: !r.participated, pointsEntered: r.participated ? '' : r.pointsEntered }
          : r
      )
    );
  };

  const handleResetScore = (playerId) => {
    setResultsData(prev =>
      prev.map(r =>
        r.playerId === playerId
          ? { ...r, pointsEntered: '' }
          : r
      )
    );
  };

  // Knockout mode handlers
  const handleAddToElimination = async (playerId) => {
    if (eliminationOrder.includes(playerId)) return;
    const newOrder = [...eliminationOrder, playerId];
    setEliminationOrder(newOrder);

    try {
      await updateEliminationOrder(gameId, newOrder);
    } catch (err) {
      console.error('Error updating elimination order:', err);
      setError('Failed to save elimination order');
    }
  };

  const handleRemoveFromElimination = async (playerId) => {
    const newOrder = eliminationOrder.filter(id => id !== playerId);
    setEliminationOrder(newOrder);

    try {
      await updateEliminationOrder(gameId, newOrder);
    } catch (err) {
      console.error('Error updating elimination order:', err);
      setError('Failed to save elimination order');
    }
  };

  const handleReorderElimination = async (fromIndex, toIndex) => {
    const newOrder = [...eliminationOrder];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setEliminationOrder(newOrder);

    try {
      await updateEliminationOrder(gameId, newOrder);
    } catch (err) {
      console.error('Error updating elimination order:', err);
      setError('Failed to save elimination order');
    }
  };

  const handleDrawOrder = async () => {
    setError('');
    setSuccess('');
    setDrawing(true);

    try {
      const playerIds = players.map(p => p.id);
      await drawPlayingOrder(gameId, playerIds, currentPlayer.id);
      setSuccess('Playing order has been drawn!');
      await loadGameData();
    } catch (err) {
      console.error('Error drawing order:', err);
      setError('Failed to draw order: ' + err.message);
    } finally {
      setDrawing(false);
    }
  };

  const handleResetGame = async () => {
    if (!window.confirm('Are you sure you want to reset this game? This will delete all results and return the game to planned status. This action cannot be undone.')) {
      return;
    }

    setError('');
    setSuccess('');
    setResetting(true);

    try {
      await resetGame(gameId);
      setSuccess('Game has been reset successfully!');

      // Refresh competition data
      await refreshCompetition();

      // Reload game data
      await loadGameData();
    } catch (err) {
      console.error('Error resetting game:', err);
      setError('Failed to reset game: ' + err.message);
    } finally {
      setResetting(false);
    }
  };

  const handleMarkAllParticipated = () => {
    setResultsData(prev =>
      prev.map(r => ({ ...r, participated: true }))
    );
  };

  const handleRateGame = async (rating) => {
    if (!currentPlayer || savingRating) return;

    setSavingRating(true);
    try {
      await rateGame(gameId, currentPlayer.id, rating, game?.gameSectionId);
      setPlayerRating(rating);

      // Reload average rating
      const avgRating = await getAverageRatingForGame(gameId);
      setAverageRating(avgRating);

      setSuccess('Rating saved!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Error saving rating:', err);
      setError('Failed to save rating');
    } finally {
      setSavingRating(false);
    }
  };

  // Helper to parse ISO timestamp into date and time components
  const parseDateTime = (isoString) => {
    if (!isoString) return { date: '', hour: '', minute: '' };
    const dateObj = new Date(isoString);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hour = String(dateObj.getHours()).padStart(2, '0');
    const minute = String(dateObj.getMinutes()).padStart(2, '0');
    return {
      date: `${year}-${month}-${day}`,
      hour,
      minute
    };
  };

  const handleStartEditGame = () => {
    setEditedGameName(game.name);
    setEditedCategoryId(game.categoryId);
    setEditedGameDate(game.date || '');
    setEditedGameTime(game.startTime || '');
    setEditedKnockoutMode(game.knockoutMode || false);
    setEditedDurationMinutes(game.durationMinutes || '');

    // Parse actual start time
    const startParsed = parseDateTime(game.actualStartTime);
    setEditedStartDate(startParsed.date);
    setEditedStartHour(startParsed.hour);
    setEditedStartMinute(startParsed.minute);

    // Parse actual end time
    const endParsed = parseDateTime(game.actualEndTime);
    setEditedEndDate(endParsed.date);
    setEditedEndHour(endParsed.hour);
    setEditedEndMinute(endParsed.minute);

    setIsEditingGame(true);
    setError('');
    setSuccess('');
  };

  const handleCancelEditGame = () => {
    setIsEditingGame(false);
    setEditedGameName('');
    setEditedCategoryId('');
    setEditedGameDate('');
    setEditedGameTime('');
    setEditedKnockoutMode(false);
    setEditedDurationMinutes('');
    setEditedStartDate('');
    setEditedStartHour('');
    setEditedStartMinute('');
    setEditedEndDate('');
    setEditedEndHour('');
    setEditedEndMinute('');
    setError('');
  };

  const handleSaveGameEdit = async () => {
    if (!editedGameName.trim()) {
      setError('Game name cannot be empty');
      return;
    }

    if (!editedCategoryId) {
      setError('Please select a category');
      return;
    }

    setError('');
    setSuccess('');
    setSavingGameEdit(true);

    try {
      const updates = {
        name: editedGameName.trim(),
        categoryId: editedCategoryId,
        date: editedGameDate,
        startTime: editedGameTime || null,
        knockoutMode: editedKnockoutMode
      };

      // Build actual start time from components
      if (editedStartDate && editedStartHour && editedStartMinute) {
        const startDateTime = `${editedStartDate}T${editedStartHour}:${editedStartMinute}:00`;
        updates.actualStartTime = new Date(startDateTime).toISOString();
      }

      // Build actual end time from components
      if (editedEndDate && editedEndHour && editedEndMinute) {
        const endDateTime = `${editedEndDate}T${editedEndHour}:${editedEndMinute}:00`;
        updates.actualEndTime = new Date(endDateTime).toISOString();

        // Calculate duration if both times are set
        if (updates.actualStartTime) {
          const start = new Date(updates.actualStartTime);
          const end = new Date(updates.actualEndTime);
          const durationMs = end.getTime() - start.getTime();
          updates.durationMinutes = Math.round(durationMs / 60000);
        }
      }

      // Determine status based on timer state
      if (updates.actualStartTime && updates.actualEndTime) {
        updates.status = 'played';
      } else if (updates.actualStartTime && !updates.actualEndTime) {
        updates.status = 'playing';
      }

      await updateGame(gameId, updates);

      setSuccess('Game updated successfully!');
      await loadGameData();
      await refreshCompetition();
      setIsEditingGame(false);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating game:', err);
      setError('Failed to update game: ' + err.message);
    } finally {
      setSavingGameEdit(false);
    }
  };

  const handleStartTimer = async () => {
    setError('');
    setSuccess('');
    setStartingTimer(true);

    try {
      await updateGame(gameId, {
        actualStartTime: new Date().toISOString(),
        status: 'playing'
      });

      setSuccess('Game timer started!');
      await loadGameData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error starting timer:', err);
      setError('Failed to start timer: ' + err.message);
    } finally {
      setStartingTimer(false);
    }
  };

  const handleEndTimer = async () => {
    setError('');
    setSuccess('');
    setEndingTimer(true);

    try {
      const endTime = new Date().toISOString();
      const startTime = new Date(game.actualStartTime);
      const durationMs = Date.now() - startTime.getTime();
      const durationMinutes = Math.round(durationMs / 60000);

      await updateGame(gameId, {
        actualEndTime: endTime,
        durationMinutes: durationMinutes,
        status: 'played'
      });

      setSuccess(`Game timer ended! Duration: ${formatDuration(durationMinutes)}`);
      await loadGameData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error ending timer:', err);
      setError('Failed to end timer: ' + err.message);
    } finally {
      setEndingTimer(false);
    }
  };

  const handleClearTimer = async () => {
    if (!window.confirm('Are you sure you want to clear the timer? This will reset the start and end times.')) {
      return;
    }

    setError('');
    setSuccess('');
    setClearingTimer(true);

    try {
      await updateGame(gameId, {
        actualStartTime: null,
        actualEndTime: null,
        durationMinutes: null,
        status: 'planned'
      });

      setSuccess('Timer cleared!');
      await loadGameData();
      await refreshCompetition();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error clearing timer:', err);
      setError('Failed to clear timer: ' + err.message);
    } finally {
      setClearingTimer(false);
    }
  };

  const handleDeleteGame = async () => {
    if (!window.confirm('Are you sure you want to delete this game? This will remove all results and cannot be undone.')) {
      return;
    }

    setError('');
    setSuccess('');
    setDeletingGame(true);

    try {
      // First delete all results for this game
      await deleteAllResultsForGame(gameId);

      // Reset game to planned status so we can delete it
      await updateGame(gameId, {
        status: 'planned',
        actualStartTime: null,
        actualEndTime: null,
        durationMinutes: null,
        drawnOrder: null,
        drawnAt: null,
        drawnBy: null,
        drawVersion: 0
      });

      // Now delete the game
      await deletePendingGame(gameId);

      setSuccess('Game deleted!');
      await refreshCompetition();

      // Navigate back to competition
      navigate(`/competition/${competitionId}`);
    } catch (err) {
      console.error('Error deleting game:', err);
      setError('Failed to delete game: ' + err.message);
    } finally {
      setDeletingGame(false);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getElapsedTime = () => {
    if (!game?.actualStartTime) return null;

    const startTime = new Date(game.actualStartTime);
    const endTime = game.actualEndTime ? new Date(game.actualEndTime) : new Date(currentTime);
    const durationMs = endTime.getTime() - startTime.getTime();
    const totalSeconds = Math.floor(durationMs / 1000);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleSaveResults = async () => {
    setError('');
    setSuccess('');

    let resultsToSave;

    if (game.knockoutMode) {
      // Knockout mode validation
      const participants = resultsData.filter(r => r.participated);

      if (eliminationOrder.length !== participants.length) {
        setError('All participants must be in the elimination order');
        return;
      }

      // Prepare knockout results - points based on elimination position
      resultsToSave = resultsData.map(r => {
        if (!r.participated) {
          // Non-participants will get average (calculated by backend)
          return {
            playerId: r.playerId,
            competitionId,
            categoryId: game.categoryId,
            participated: false,
            pointsEntered: null,
            eliminationPosition: null
          };
        }

        // Find elimination position (1-indexed)
        const eliminationIndex = eliminationOrder.indexOf(r.playerId);
        const eliminationPosition = eliminationIndex + 1; // 1st eliminated = position 1
        const points = eliminationPosition; // 1st out = 1 point, 2nd out = 2 points, etc.

        return {
          playerId: r.playerId,
          competitionId,
          categoryId: game.categoryId,
          participated: true,
          pointsEntered: points,
          eliminationPosition: eliminationPosition
        };
      });
    } else {
      // Regular mode validation and preparation
      const resultsToValidate = resultsData.map(r => ({
        playerId: r.playerId,
        participated: r.participated,
        pointsEntered: r.participated ? parseFloat(r.pointsEntered) : null
      }));

      const validationErrors = validateGameResults(players, resultsToValidate);

      if (validationErrors.length > 0) {
        setError(validationErrors.join(', '));
        return;
      }

      resultsToSave = resultsData.map(r => ({
        playerId: r.playerId,
        competitionId,
        categoryId: game.categoryId,
        participated: r.participated,
        pointsEntered: r.participated ? parseFloat(r.pointsEntered) : null,
        eliminationPosition: null
      }));
    }

    setSaving(true);

    try {
      await saveGameResults(gameId, resultsToSave);

      // Mark game as played if it was planned
      if (game.status === 'planned') {
        await updateGameStatus(gameId, 'played');
      }

      setSuccess('Results saved successfully!');

      // Refresh competition data to update leaderboards
      await refreshCompetition();

      // Reload game data
      await loadGameData();

      setSaving(false);

      // Navigate back after a short delay
      setTimeout(() => {
        navigate(`/competition/${competitionId}`);
      }, 1500);
    } catch (err) {
      console.error('Error saving results:', err);
      setError('Failed to save results: ' + err.message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading game...</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-600">Game not found</p>
      </div>
    );
  }

  const canEdit = isOrganizer && game.status !== 'voided';

  return (
    <div className="min-h-screen bg-gray-50">
      <HamburgerMenu />
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {isEditingGame ? (
            // Edit Mode
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Game Name</label>
                <Input
                  value={editedGameName}
                  onChange={(e) => setEditedGameName(e.target.value)}
                  placeholder="Enter game name"
                  className="max-w-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editedCategoryId}
                  onChange={(e) => setEditedCategoryId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-md"
                >
                  {categories.filter(c => c.status === 'active').map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <Input
                  type="date"
                  value={editedGameDate}
                  onChange={(e) => setEditedGameDate(e.target.value)}
                  className="max-w-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time (Optional)</label>
                <select
                  value={editedGameTime}
                  onChange={(e) => setEditedGameTime(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-md"
                >
                  <option value="">No specific time</option>
                  {generateTimeOptions().map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editedKnockoutMode}
                    onChange={(e) => setEditedKnockoutMode(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Knockout Mode
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Players are eliminated one by one. Points awarded based on elimination order (1st out = 1 point, 2nd out = 2 points, etc.)
                </p>
              </div>
              {game.actualStartTime && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-900">Game Timer (24-hour format)</h3>

                  {/* Actual Start Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Actual Start Time
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="date"
                        value={editedStartDate}
                        onChange={(e) => setEditedStartDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <select
                        value={editedStartHour}
                        onChange={(e) => setEditedStartHour(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Hour</option>
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = String(i).padStart(2, '0');
                          return <option key={hour} value={hour}>{hour}</option>;
                        })}
                      </select>
                      <span className="text-gray-600">:</span>
                      <select
                        value={editedStartMinute}
                        onChange={(e) => setEditedStartMinute(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Min</option>
                        {Array.from({ length: 60 }, (_, i) => {
                          const minute = String(i).padStart(2, '0');
                          return <option key={minute} value={minute}>{minute}</option>;
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Actual End Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Actual End Time
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="date"
                        value={editedEndDate}
                        onChange={(e) => setEditedEndDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <select
                        value={editedEndHour}
                        onChange={(e) => setEditedEndHour(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Hour</option>
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = String(i).padStart(2, '0');
                          return <option key={hour} value={hour}>{hour}</option>;
                        })}
                      </select>
                      <span className="text-gray-600">:</span>
                      <select
                        value={editedEndMinute}
                        onChange={(e) => setEditedEndMinute(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Min</option>
                        {Array.from({ length: 60 }, (_, i) => {
                          const minute = String(i).padStart(2, '0');
                          return <option key={minute} value={minute}>{minute}</option>;
                        })}
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Edit the times to correct the game duration. Duration will be calculated automatically.
                    </p>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveGameEdit}
                  disabled={savingGameEdit}
                  size="sm"
                >
                  {savingGameEdit ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  onClick={handleCancelEditGame}
                  variant="outline"
                  size="sm"
                  disabled={savingGameEdit}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            // View Mode
            <>
              <div className="flex items-center gap-3 mb-2">
                {category && <span className="text-4xl">{category.icon}</span>}
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900">{game.name}</h1>
                  <p className="text-sm text-gray-600 mt-1">
                    {category?.name} • {formatDateTime(game.date, game.startTime)}
                  </p>
                </div>
                {isOrganizer && game.status !== 'voided' && (
                  <Button
                    onClick={handleStartEditGame}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    Edit Game
                  </Button>
                )}
              </div>
              <div className="mt-2 flex items-center gap-3">
                {game.status === 'played' && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Played</span>
                )}
                {game.status === 'planned' && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Planned</span>
                )}
                {game.status === 'voided' && (
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Voided</span>
                )}
                {isOrganizer && game.status === 'played' && (
                  <Button
                    onClick={handleResetGame}
                    disabled={resetting}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    {resetting ? 'Resetting...' : 'Reset Game'}
                  </Button>
                )}
              </div>

              {/* Game Timer Section */}
              {isOrganizer && game.status !== 'voided' && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      {!game.actualStartTime ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Game Timer:</span>
                          <span className="text-sm text-gray-500">Not started</span>
                        </div>
                      ) : game.actualEndTime ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Game Duration:</span>
                          <span className="text-lg font-bold text-green-700">
                            {getElapsedTime()}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({formatDuration(game.durationMinutes)})
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Elapsed Time:</span>
                          <span className="text-lg font-bold text-blue-700 tabular-nums">
                            {getElapsedTime()}
                          </span>
                          <span className="ml-2 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!game.actualStartTime && (
                        <Button
                          onClick={handleStartTimer}
                          disabled={startingTimer}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {startingTimer ? 'Starting...' : 'Start Timer'}
                        </Button>
                      )}
                      {game.actualStartTime && !game.actualEndTime && (
                        <Button
                          onClick={handleEndTimer}
                          disabled={endingTimer}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {endingTimer ? 'Ending...' : 'End Timer'}
                        </Button>
                      )}
                      {game.actualStartTime && (
                        <Button
                          onClick={handleClearTimer}
                          disabled={clearingTimer}
                          variant="outline"
                          size="sm"
                          className="text-gray-600"
                        >
                          {clearingTimer ? 'Clearing...' : 'Clear Timer'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Wizard Scoreboard Link - Show for Wizard games */}
              {game.name.toLowerCase().includes('wizard') && game.drawnOrder && game.drawnOrder.length >= 3 && (
                <div className="mt-4 p-3 bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 rounded-lg">
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">🧙</span>
                    <div className="flex-1">
                      <h3 className="text-md font-bold text-purple-900">Wizard Scoreboard</h3>
                      <p className="text-xs text-purple-700">
                        Track bids, tricks, and scores
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate(`/competition/${competitionId}/game/${gameId}/wizard`)}
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Open Scoreboard
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {game.notes && (
          <Card className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
            <p className="text-gray-700">{game.notes}</p>
          </Card>
        )}

        {/* Game Rating Section - Show for played games */}
        {game.status === 'played' && currentPlayer && (
          <Card className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Rate this Game</h3>
                <p className="text-xs text-gray-500">How much did you enjoy playing?</p>
              </div>
              {averageRating.count > 0 && (
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-500 text-lg">★</span>
                    <span className="font-bold text-gray-900">{averageRating.average.toFixed(1)}</span>
                  </div>
                  <p className="text-xs text-gray-500">{averageRating.count} rating{averageRating.count !== 1 ? 's' : ''}</p>
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRateGame(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  disabled={savingRating}
                  className={`text-3xl transition-transform hover:scale-110 ${savingRating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={
                    (hoverRating || playerRating) >= star
                      ? 'text-yellow-500'
                      : 'text-gray-300'
                  }>
                    ★
                  </span>
                </button>
              ))}
              {playerRating && (
                <span className="ml-3 text-sm text-gray-600">
                  Your rating: {playerRating}/5
                </span>
              )}
              {savingRating && (
                <span className="ml-3 text-sm text-gray-500">Saving...</span>
              )}
            </div>
          </Card>
        )}

        {/* Category Ranking Section */}
        {currentPlayer && categoryStats && (
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {category?.icon} {category?.name} Standings
            </h3>

            {/* Player's Current Stats */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Your Position</p>
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-bold text-blue-700">
                      #{categoryStats.playerStats.rank}
                    </div>
                    <div>
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">{categoryStats.playerStats.score}</span> placement pts
                      </p>
                      <p className="text-xs text-gray-600">
                        {categoryStats.playerStats.actualScore.toFixed(2)} total score | {categoryStats.playerStats.gamesPlayed} games
                      </p>
                    </div>
                  </div>
                </div>
                {categoryStats.playerStats.rank === 1 && (
                  <div className="text-5xl">🏆</div>
                )}
                {categoryStats.playerStats.rank === 2 && (
                  <div className="text-5xl">🥈</div>
                )}
                {categoryStats.playerStats.rank === 3 && (
                  <div className="text-5xl">🥉</div>
                )}
              </div>
            </div>

            {/* Insights for Improvement */}
            {categoryStats.insights.length > 0 && (
              <div className="mb-4 space-y-2">
                {categoryStats.insights.map((insight, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg ${
                      insight.type === 'leading'
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : insight.type === 'podium'
                        ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                        : insight.type === 'bestcase'
                        ? 'bg-purple-50 border-2 border-purple-300 text-purple-900'
                        : 'bg-blue-50 border border-blue-200 text-blue-800'
                    }`}
                  >
                    <p className="text-sm font-medium">
                      {insight.type === 'leading' && '🌟 '}
                      {insight.type === 'podium' && '🎯 '}
                      {insight.type === 'improve' && '💡 '}
                      {insight.type === 'bestcase' && '🚀 '}
                      {insight.message}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Perfect Game Scenario Breakdown - Only show for planned/playing games */}
            {categoryStats.bestCaseBreakdown && (game.status === 'planned' || game.status === 'playing') && (
              <div className="mb-4 bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
                <h4 className="text-sm font-bold text-purple-900 mb-1">Perfect Game Scenario</h4>
                <p className="text-xs text-purple-700 mb-3 italic">
                  What would happen if results balance the category: You win, rank #1 finishes last, rank #2 finishes 2nd to last, etc.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-purple-300">
                        <th className="text-left py-2 px-2 text-purple-900">Player</th>
                        <th className="text-center py-2 px-2 text-purple-900">Current<br/>Rank</th>
                        <th className="text-center py-2 px-2 text-purple-900">Current<br/>Points</th>
                        <th className="text-center py-2 px-2 text-purple-900">Game<br/>Position</th>
                        <th className="text-center py-2 px-2 text-purple-900">Points<br/>Earned</th>
                        <th className="text-center py-2 px-2 text-purple-900">New<br/>Total</th>
                        <th className="text-center py-2 px-2 text-purple-900">New<br/>Rank</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryStats.bestCaseBreakdown.participatingPlayers
                        .map((player) => {
                          const newRanking = categoryStats.bestCaseBreakdown.newRankings.find(p => p.playerId === player.playerId);
                          return { ...player, newRank: newRanking?.bestCaseRank };
                        })
                        .sort((a, b) => a.newRank - b.newRank)
                        .map((player) => {
                          const isCurrentPlayer = player.playerId === currentPlayer.id;
                          return (
                            <tr
                              key={player.playerId}
                              className={`border-b border-purple-100 ${isCurrentPlayer ? 'bg-purple-200 font-bold' : ''}`}
                            >
                              <td className="py-2 px-2 text-purple-900">
                                {player.playerName}
                                {isCurrentPlayer && ' (You)'}
                              </td>
                              <td className="text-center py-2 px-2 text-purple-900">#{player.currentRank}</td>
                              <td className="text-center py-2 px-2 text-purple-900">{player.currentScore}</td>
                              <td className="text-center py-2 px-2 text-purple-900">{player.gamePosition}</td>
                              <td className="text-center py-2 px-2 text-purple-900">+{player.gamePoints}</td>
                              <td className="text-center py-2 px-2 text-purple-900 font-semibold">{player.newScore}</td>
                              <td className="text-center py-2 px-2 text-purple-900 font-semibold">#{player.newRank}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top 5 Leaderboard */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Top 5{categoryStats.playerStats.rank > 5 ? ' + You' : ''}</p>
              <div className="space-y-1">
                {(() => {
                  const top5 = categoryStats.leaderboard.slice(0, 5);
                  const currentPlayerInTop5 = top5.some(p => p.playerId === currentPlayer.id);
                  const playersToShow = currentPlayerInTop5 ? top5 : [...top5, categoryStats.playerStats];

                  return playersToShow.map((player, index) => {
                    const isCurrentPlayer = player.playerId === currentPlayer.id;
                    const showDivider = index === 5; // Show divider before current player if not in top 5

                    return (
                      <React.Fragment key={player.playerId}>
                        {showDivider && (
                          <div className="border-t border-gray-300 my-2"></div>
                        )}
                        <div
                          className={`flex items-center gap-3 p-2 rounded ${
                            isCurrentPlayer ? 'bg-blue-100 font-semibold' : 'bg-gray-50'
                          }`}
                        >
                          <div className="w-6 text-center text-sm font-bold text-gray-600">
                            {player.rank}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">
                              {player.playerName}
                              {isCurrentPlayer && ' (You)'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-gray-900">{player.score} pts</p>
                            <p className="text-xs text-gray-500">{player.actualScore.toFixed(2)} score</p>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            </div>
          </Card>
        )}

        {/* Playing Order Section */}
        <Card className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Playing Order</h3>
            {isOrganizer && game.status === 'planned' && (
              <Button
                onClick={handleDrawOrder}
                disabled={drawing}
                variant={game.drawnOrder ? 'outline' : 'primary'}
                size="sm"
              >
                {drawing ? 'Drawing...' : game.drawnOrder ? 'Redraw Order' : 'Draw Order'}
              </Button>
            )}
          </div>

          {game.drawnOrder && game.drawnOrder.length > 0 ? (
            <div className="space-y-2">
              {game.drawnOrder.map((playerId, index) => {
                const player = players.find(p => p.id === playerId);
                return (
                  <div
                    key={playerId}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <Avatar player={player} size="sm" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{player?.displayName || 'Unknown Player'}</p>
                    </div>
                  </div>
                );
              })}
              {game.drawnAt && (
                <p className="text-xs text-gray-500 mt-3">
                  Drawn on {new Date(game.drawnAt.seconds * 1000).toLocaleString()}
                  {game.drawVersion && ` (v${game.drawVersion})`}
                </p>
              )}
              {game.status === 'played' && (
                <p className="text-xs text-blue-600 mt-2">
                  ✓ Order is locked (game has been played)
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No playing order has been drawn yet</p>
              {isOrganizer && game.status === 'planned' && (
                <p className="text-xs mt-2">Click "Draw Order" to randomly assign playing positions</p>
              )}
            </div>
          )}
        </Card>

        {/* Results Entry */}
        <Card title={canEdit ? "Enter Results" : "Results"}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
              {success}
            </div>
          )}

          {game.knockoutMode && canEdit && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800 font-medium">🎯 Knockout Mode</p>
              <p className="text-xs text-blue-700 mt-1">
                Set the elimination order from first out to last standing. Points are awarded automatically (1st out = 1 pt, 2nd out = 2 pts, etc.)
              </p>
            </div>
          )}

          {!game.knockoutMode && canEdit && (
            <div className="mb-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={handleMarkAllParticipated}>
                Mark All Participated
              </Button>
              {averagePreview !== null && (
                <div className="ml-auto text-sm text-gray-600 flex items-center">
                  <span className="font-medium">Average for non-participants:</span>
                  <span className="ml-2 font-bold text-blue-600">{averagePreview.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {game.knockoutMode ? (
            canEdit ? (
              // Knockout Mode Edit UI
              <div className="space-y-4">
                {/* Participants who can be eliminated */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Available Players</h3>
                  <div className="space-y-2">
                    {resultsData.filter(r => r.participated && !eliminationOrder.includes(r.playerId)).map(result => {
                      const player = players.find(p => p.id === result.playerId);
                      return (
                        <div key={result.playerId} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <Avatar player={player} size="sm" />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{result.playerName}</p>
                          </div>
                          <Button size="sm" onClick={() => handleAddToElimination(result.playerId)}>
                            Add to Elimination
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Elimination Order */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Elimination Order (1st out → Last standing)
                  </h3>
                  {eliminationOrder.length > 0 ? (
                    <div className="space-y-2">
                      {eliminationOrder.map((playerId, index) => {
                        const player = players.find(p => p.id === playerId);
                        const points = index + 1;
                        return (
                          <div key={playerId} className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                              {points}
                            </div>
                            <Avatar player={player} size="sm" />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{player?.displayName}</p>
                              <p className="text-xs text-gray-500">{points} point{points !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="flex gap-1">
                              {index > 0 && (
                                <Button size="sm" variant="outline" onClick={() => handleReorderElimination(index, index - 1)}>
                                  ↑
                                </Button>
                              )}
                              {index < eliminationOrder.length - 1 && (
                                <Button size="sm" variant="outline" onClick={() => handleReorderElimination(index, index + 1)}>
                                  ↓
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => handleRemoveFromElimination(playerId)}>
                                Remove
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No players eliminated yet</p>
                  )}
                </div>

                {/* Non-participants */}
                {resultsData.filter(r => !r.participated).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Non-Participants</h3>
                    <div className="space-y-2">
                      {resultsData.filter(r => !r.participated).map(result => {
                        const player = players.find(p => p.id === result.playerId);
                        return (
                          <div key={result.playerId} className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg opacity-60">
                            <Avatar player={player} size="sm" />
                            <div className="flex-1">
                              <p className="font-medium text-gray-700">{result.playerName}</p>
                              <p className="text-xs text-gray-500">Did not play</p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => handleParticipationToggle(result.playerId)}>
                              Mark as Participated
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Knockout Mode View UI (read-only)
              <div className="space-y-4">
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800 font-medium">🎯 Knockout Mode</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Elimination order from first out to last standing
                  </p>
                </div>

                {/* Elimination Order (Read-only) */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Elimination Order
                  </h3>
                  {eliminationOrder.length > 0 ? (
                    <div className="space-y-2">
                      {eliminationOrder.map((playerId, index) => {
                        const player = players.find(p => p.id === playerId);
                        const points = index + 1;
                        return (
                          <div key={playerId} className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                              {points}
                            </div>
                            <Avatar player={player} size="sm" />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{player?.displayName}</p>
                              <p className="text-xs text-gray-500">{points} point{points !== 1 ? 's' : ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No elimination order recorded yet</p>
                  )}
                </div>

                {/* Non-participants (Read-only) */}
                {resultsData.filter(r => !r.participated).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Non-Participants</h3>
                    <div className="space-y-2">
                      {resultsData.filter(r => !r.participated).map(result => {
                        const player = players.find(p => p.id === result.playerId);
                        return (
                          <div key={result.playerId} className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg opacity-60">
                            <Avatar player={player} size="sm" />
                            <div className="flex-1">
                              <p className="font-medium text-gray-700">{result.playerName}</p>
                              <p className="text-xs text-gray-500">Did not play</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          ) : (
            // Regular Mode UI
            <div className="space-y-3">
            {resultsData.map(result => {
              const player = players.find(p => p.id === result.playerId);
              return (
                <div key={result.playerId} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Avatar player={player} size="md" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{result.playerName}</p>
                  </div>

                  {canEdit ? (
                    <>
                      <div className="w-32">
                        <Input
                          type="number"
                          step="0.01"
                          value={result.pointsEntered}
                          onChange={(e) => handlePointsChange(result.playerId, e.target.value)}
                          placeholder="Points"
                          disabled={!result.participated}
                          className="mb-0"
                        />
                      </div>
                      {result.pointsEntered && result.participated && (
                        <button
                          onClick={() => handleResetScore(result.playerId)}
                          className="text-xs text-red-600 hover:text-red-700 underline"
                          title="Clear score"
                        >
                          Reset
                        </button>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!result.participated}
                          onChange={() => handleParticipationToggle(result.playerId)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">Did not play</span>
                      </label>
                      {!result.participated && averagePreview !== null && (
                        <div className="text-sm text-gray-500">
                          Will receive: <span className="font-semibold">{averagePreview.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-right">
                        {result.participated ? (
                          <p className="text-lg font-bold text-gray-900">
                            {result.pointsEntered ? parseFloat(result.pointsEntered).toFixed(2) : '-'}
                          </p>
                        ) : (
                          <div>
                            <p className="text-sm text-gray-500">Did not play</p>
                            {averagePreview !== null && (
                              <p className="text-sm font-semibold text-gray-700">
                                Avg: {averagePreview.toFixed(2)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          )}

          {canEdit && (
            <div className="mt-6 flex gap-3">
              <Button
                onClick={handleSaveResults}
                disabled={saving}
                fullWidth
              >
                {saving ? 'Saving...' : game.status === 'planned' ? 'Save & Mark as Played' : 'Update Results'}
              </Button>
              <Button
                onClick={() => navigate(`/competition/${competitionId}`)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          )}
        </Card>

        {/* Delete Game Section - Show for organizers */}
        {isOrganizer && game.status !== 'voided' && (
          <Card className="mt-6 border-red-200 bg-red-50">
            <h3 className="text-lg font-semibold text-red-900 mb-2">Danger Zone</h3>
            <p className="text-sm text-red-700 mb-4">
              Permanently delete this game and all its results. This action cannot be undone.
            </p>
            <Button
              onClick={handleDeleteGame}
              disabled={deletingGame}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingGame ? 'Deleting...' : 'Delete Game'}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GameDetails;
